import React from 'react';
import { Loader2, LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';

/**
 * The one button of the app, so buttons side by side are the same height, radius, weight and spacing whatever they
 * say. Variants: primary (the step forward: one per place), secondary (outlined: the other choices), ghost (quiet,
 * in lists and toolbars), danger (removes something). Sizes: sm 32 px, md 36 px, lg 44 px high; the text never
 * wraps, so a long label makes the button wider rather than taller.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

// border-solid: the app's global `button { border: none }` would otherwise hide the outline of the secondary button
const base =
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border-solid font-medium select-none ' +
  'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-[var(--gn-primary)] ' +
  'disabled:opacity-50 disabled:cursor-not-allowed';

const variants: Record<ButtonVariant, string> = {
  primary: 'bg-gn-blue text-gn-inverse hover:bg-gn-blue border border-transparent',
  secondary: 'bg-gn-surface text-gn-primary border border-gn hover:bg-gn-hover',
  ghost: 'bg-transparent text-gn-secondary border border-transparent hover:bg-gn-hover hover:text-gn-primary',
  danger: 'bg-gn-danger text-gn-inverse hover:bg-gn-danger border border-transparent',
};

const sizes: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-xs',
  md: 'h-9 px-4 text-sm',
  lg: 'h-11 px-5 text-sm',
};

const iconSizes: Record<ButtonSize, number> = { sm: 14, md: 16, lg: 18 };

export function buttonClass(variant: ButtonVariant = 'secondary', size: ButtonSize = 'md', extra?: string) {
  return cn(base, variants[variant], sizes[size], extra);
}

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: LucideIcon;
  /** Shows a spinner in place of the icon and disables the button. */
  loading?: boolean;
  /** Stretches to the width of its container. */
  block?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'secondary', size = 'md', icon: Icon, loading = false, block = false, className, children, disabled, type, ...rest }, ref) => (
    <button ref={ref} type={type ?? 'button'} disabled={disabled || loading}
            className={buttonClass(variant, size, cn(block && 'w-full', className))} {...rest}>
      {loading ? <Loader2 size={iconSizes[size]} className="animate-spin shrink-0" /> : Icon && <Icon size={iconSizes[size]} className="shrink-0" />}
      {children}
    </button>
  ),
);
Button.displayName = 'Button';

/** A square button holding only an icon: the label goes to title and aria-label. */
export const IconButton = React.forwardRef<HTMLButtonElement, Omit<ButtonProps, 'children' | 'block'> & { label: string }>(
  ({ variant = 'ghost', size = 'md', icon: Icon, label, loading, className, type, disabled, ...rest }, ref) => {
    const square = size === 'sm' ? 'h-8 w-8' : size === 'lg' ? 'h-11 w-11' : 'h-9 w-9';
    return (
      <button ref={ref} type={type ?? 'button'} title={label} aria-label={label} disabled={disabled || loading}
              className={cn(base, variants[variant], square, 'p-0', className)} {...rest}>
        {loading ? <Loader2 size={iconSizes[size]} className="animate-spin" /> : Icon && <Icon size={iconSizes[size]} />}
      </button>
    );
  },
);
IconButton.displayName = 'IconButton';

export default Button;
